import { FunctionComponent, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components/Modals/Modal";
import { ButtonColor } from "@/components/Button/types";

interface DownstreamApprovedModalProps {
	isOpened: boolean;
	/** The steps the server said would be reset — its answer, not a client guess. */
	steps: number[];
	isLoading?: boolean;
	onCancel: () => void;
	onConfirm: () => void;
}

/**
 * The 409 DOWNSTREAM_APPROVED dialog. The server refuses a call that would discard
 * approved downstream work; confirming here retries the same call with `force: true`.
 *
 * The mockup used `window.confirm`; this says the same thing in the product's own modal,
 * and names the exact steps rather than "everything after this".
 */
export const DownstreamApprovedModal: FunctionComponent<DownstreamApprovedModalProps> = ({
	isOpened,
	steps,
	isLoading,
	onCancel,
	onConfirm,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) => t(`videoWizard.${key}`, options ?? {}),
		[t]
	);

	return (
		<Modal
			cancelButtonText={ts("confirmReset.cancel")}
			handleClose={onCancel}
			handleSave={onConfirm}
			isLoading={isLoading}
			isOpened={isOpened}
			size="sm"
			submitButtonColor={ButtonColor.DANGER}
			submitButtonText={ts("confirmReset.confirm")}
			title={ts("confirmReset.title")}
		>
			<div className="space-y-3 text-sm text-text">
				<p>{ts("confirmReset.body")}</p>
				{!!steps.length && (
					<p className="rounded-card bg-surface px-3 py-2 text-xs font-semibold text-text">
						{ts("confirmReset.affectedSteps", { steps: steps.join(", ") })}
					</p>
				)}
				{/* The one reassurance that matters: resetting work never resets its cost record. */}
				<p className="text-xs text-subtle">{ts("confirmReset.costsRemain")}</p>
			</div>
		</Modal>
	);
};
